import { useEffect, useMemo } from "react";
import { Form, Row, Col, Switch, message, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  addEnterprisePoc,
  getEnterprisePocs,
  getDesignations,
  checkEnterprisePocEmail,
} from "../../../redux/slices/enterpriseSlice";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomInput from "../../../components/CustomInput/Input";
import CustomSelect from "../../../components/CustomSelect/CustomSelect";
import FormFooter from "../../../components/CustomForms/FormFooter";
import { ERROR_MSGS, FORM_MODAL_WIDTHS, YESORNO } from "../../../constants/constants";
import { CloseOutlined } from "@ant-design/icons";
import {
  nameRules,
  emailRules,
  phoneNumberRules,
  designationRules,
  getRemarksRules,
  REMARKS_MAX_LENGTH,
} from "../../../helpers/helpers";
import {
  isOthersDesignation,
  resolveDesignationName,
  buildDesignationOptions,
  DEPARTMENT_OPTIONS,
} from "./pocHelpers";
import PocApobAddressFields from "./PocApobAddressFields";

const { Title } = Typography;

const NewPocModal = ({ visible, onCancel, enterpriseId, enterpriseName }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { pocActionLoading, designationsData } = useSelector(
    (state) => state.enterprise,
  );
  const selectedDesignationId = Form.useWatch("designationId", form);
  const hasApob = Form.useWatch("hasApob", form) === true;
  const showCustomDesignation = isOthersDesignation(
    selectedDesignationId,
    designationsData,
  );

  const designationOptions = useMemo(
    () => buildDesignationOptions(designationsData),
    [designationsData],
  );

  useEffect(() => {
    dispatch(getDesignations());
  }, [dispatch]);

  const pocEmailRules = useMemo(
    () => [
      ...emailRules("POC"),
      {
        validateTrigger: "onBlur",
        validator: async (_, value) => {
          const trimmed = typeof value === "string" ? value.trim() : value;
          if (!trimmed) return Promise.resolve();
          if (
            !/^[a-zA-Z0-9]+(?:\.[a-zA-Z0-9]+)*@[a-zA-Z0-9]+(?:\.[a-zA-Z0-9]+)+$/.test(
              trimmed,
            )
          ) {
            return Promise.resolve();
          }

          const res = await dispatch(
            checkEnterprisePocEmail({ entId: enterpriseId, email: trimmed }),
          );
          if (
            res?.meta?.requestStatus === "fulfilled" &&
            (res?.payload?.exists === true ||
              res?.payload?.response?.exists === true)
          ) {
            return Promise.reject(new Error("Email already exists"));
          }
          return Promise.resolve();
        },
      },
    ],
    [dispatch, enterpriseId],
  );

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleFinish = async (values) => {
    try {
      const payload = {
        enterpriseId,
        name: values.name.trim(),
        email: values.email.trim(),
        mobile: values.mobile.trim(),
        department: values.department,
        apob: values.hasApob ? values.apob.trim() : undefined,
        designation: resolveDesignationName(values, designationsData),
        remarks: values.remarks ? values.remarks.trim() : undefined,
        sendApprovalMail: values.sendApprovalMail !== false,
      };

      if (values.hasApob) {
        payload.address = {
          addressLine1: values.address.addressLine1.trim(),
          addressLine2: values.address.addressLine2?.trim() || undefined,
          city: values.address.city.trim(),
          state: values.address.state.trim(),
          pinCode: values.address.pinCode.trim(),
        };
      }

      const res = await dispatch(addEnterprisePoc(payload));
      if (
        res?.meta?.requestStatus === "fulfilled" ||
        res?.payload?.statusCode === 200 ||
        res?.payload?.statusCode === 201
      ) {
        message.success(
          `Point of Contact "${payload.name}" added successfully!`,
        );
        form.resetFields();
        onCancel();
        dispatch(getEnterprisePocs({ entId: enterpriseId }));
      } else {
        const errorMsg =
          res?.payload?.error ||
          res?.payload?.message ||
          (typeof res?.payload === "string" ? res.payload : null) ||
          "Failed to add POC";
        const isDuplicateEmail = /email already exists/i.test(String(errorMsg));
        if (isDuplicateEmail) {
          form.setFields([{ name: "email", errors: ["Email already exists"] }]);
        }
        message.error(errorMsg);
      }
    } catch (err) {
      message.error(err?.message || "An unexpected error occurred.");
    }
  };

  return (
    <CustomModal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>
            Add Point of Contact (POC) - {enterpriseName || "Enterprise"}
          </span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      closeIcon={<CloseOutlined />}
      width={FORM_MODAL_WIDTHS}
      destroyOnHidden
      centered
      content={
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{ sendApprovalMail: true }}
          className="poc-modal-form"
        >
          <Title level={5}>Contact Information</Title>

          <Row gutter={16}>
            <Col span={12}>
              <CustomInput
                name="name"
                label="Full Name"
                isRequired
                rules={nameRules("POC")}
                placeholder="e.g. Rajesh Sharma"
              />
            </Col>

            <Col span={12}>
              <CustomInput
                name="email"
                type="email"
                label="Email Address"
                isRequired
                rules={pocEmailRules}
                placeholder="e.g. rajesh@example.com"
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <CustomInput
                name="mobile"
                label="Mobile Number"
                isRequired
                allowNumbersOnly
                maxLength={10}
                rules={phoneNumberRules("POC")}
                placeholder="e.g. 9876543210"
              />
            </Col>

            <Col span={12}>
              <CustomSelect
                name="designationId"
                label="Designation / Role"
                isRequired
                showSearch
                placeholder="Select Designation"
                options={designationOptions}
                rules={[
                  { required: true, message: ERROR_MSGS.DESIGNATION_ERR_MEG },
                ]}
                onChange={(value) => {
                  if (!isOthersDesignation(value, designationsData)) {
                    form.setFieldsValue({ customDesignation: undefined });
                  }
                }}
              />
            </Col>
          </Row>

          {showCustomDesignation && (
            <CustomInput
              name="customDesignation"
              label="Enter Designation"
              isRequired
              placeholder="Enter designation"
              rules={designationRules}
            />
          )}

          <Title level={5}> Department & Additional Place of Business (APOB)</Title>

          <Row gutter={16}>
            <Col span={12}>
              <CustomSelect
                name="department"
                label="Department"
                isRequired
                showSearch
                placeholder="Select or type department"
                options={DEPARTMENT_OPTIONS}
                rules={[
                  {
                    required: true,
                    message: "Please select or enter department",
                  },
                ]}
              />
            </Col>

            <Col span={12}>
              <CustomSelect
                name="hasApob"
                label="Has Additional Place of Business (APOB)?"
                placeholder="Select Yes/No"
                isRequired
                options={YESORNO}
                rules={[
                  {
                    required: true,
                    message: "Please select whether the POC has an APOB",
                  },
                ]}
                onChange={(value) => {
                  if (!value) {
                    form.setFieldsValue({ apob: undefined, address: undefined });
                  }
                }}
              />
            </Col>
          </Row>

          {hasApob && <PocApobAddressFields />}

          <CustomInput
            name="remarks"
            label="Remarks / Notes"
            isTextArea
            rows={2}
            maxLength={REMARKS_MAX_LENGTH}
            placeholder="Additional notes about this contact person (optional)"
            rules={getRemarksRules({ required: false })}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              margin: "16px 0 24px",
              background: "#eff6ff",
              padding: "10px 16px",
              borderRadius: 6,
            }}
          >
            <div>
              <div style={{ fontWeight: 600, color: "#1e3a8a", fontSize: 13 }}>
                Send Approval Notification Email
              </div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                Dispatches an onboarding & verification email to the POC with
                department details.
              </div>
            </div>
            <Form.Item name="sendApprovalMail" valuePropName="checked" noStyle>
              <Switch defaultChecked />
            </Form.Item>
          </div>

          <FormFooter
            onCancelClick={handleCancel}
            submitLoading={pocActionLoading}
            btnName="Add Point of Contact"
          />
        </Form>
      }
    />
  );
};

export default NewPocModal;
